import React, { useState, useEffect, useMemo, useRef } from "react";
import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter";
import {
  InstantSearch,
  Configure,
  useHits,
  useSearchBox,
} from "react-instantsearch-hooks";
import { Highlight } from "react-instantsearch";
import { debounce } from "@algolia/autocomplete-shared";
import { useDispatch, useSelector } from "react-redux";
import { addToast } from "@/store/app/action";
import config from "@/config";
import {
  formatValue,
  getFilteredMedianPrice,
  getPriceIndicatorType,
} from "@/utils/Utils";
import Badge from "@/components/Badges";
import PaginationNumeric from "@/components/PaginationNumeric";
import {
  getProductMappings,
  updateProductMapping,
  deleteProductMapping,
  ignoreProductMapping,
  unignoreProductMapping,
  getProductsBySKUs,
} from "@/api/externalFeeds";
import CustomProduct from "@/pages/products/customProduct.jsx";
import {
  SecondaryButton,
  SecondaryDangerButton,
  NakedButton,
} from "@/components/Buttons.jsx";

// Search Results Component that works within InstantSearch context
const SearchResults = ({
  onSelectProduct,
  currentQuery,
  showAllProducts = false,
  posPrice = null,
}) => {
  const { hits } = useHits();

  // Show results if we have a search query OR if we want to show all products
  if (!currentQuery && !showAllProducts) return null;

  return (
    <div className="border-t border-slate-200 dark:border-slate-600">
      <div className="px-3 py-2 text-xs font-medium text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-theme-surface-accent">
        {currentQuery
          ? `Search Results (${hits.length})`
          : `All Products (${hits.length})`}
      </div>
      {hits.map((hit) => {
        const priceKeys = Object.keys(hit).filter(
          (key) => key.endsWith("_price") && !key.endsWith("_buy_price")
        );
        const priceValues = priceKeys.map((key) => hit[key] || 0);
        const avgPrice = getFilteredMedianPrice(priceValues);

        // Calculate price difference if POS price is available
        let priceDifference = null;
        let priceDifferencePercent = null;
        let priceIndicatorType = null;

        if (posPrice && avgPrice > 0) {
          // Calculate percentage from retailer's perspective
          // Positive = retailer charges more than average
          // Negative = retailer charges less than average
          priceDifferencePercent = ((posPrice - avgPrice) / avgPrice) * 100;
          priceIndicatorType = getPriceIndicatorType(priceDifferencePercent);
        }

        return (
          <div
            key={hit.sku_id}
            className="px-3 py-2 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 flex items-center space-x-3 border-b border-slate-100 dark:border-slate-700"
            onClick={() => onSelectProduct(hit)}
          >
            {hit.image && (
              <img
                src={hit.image}
                alt={hit.title}
                className="w-8 h-8 object-cover rounded flex-shrink-0"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-800 dark:text-slate-100 truncate flex items-center gap-2">
                <Highlight attribute="title" hit={hit} />{" "}
                {hit.volume_label && (
                  <span>
                    - <Highlight attribute="volume_label" hit={hit} />
                  </span>
                )}
                {hit.is_custom && (
                  <Badge color="purple" slim>Custom</Badge>
                )}
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-400 truncate">
                <Highlight attribute="subtitle" hit={hit} /> -{" "}
                <Highlight attribute="sku_id" hit={hit} />
              </div>
            </div>
            <div className="flex items-center space-x-2 flex-shrink-0">
              <span className="text-sm text-slate-600 dark:text-slate-400">
                Avg {formatValue(avgPrice)}
              </span>
              {priceIndicatorType && (
                <Badge
                  color={
                    priceIndicatorType === "match"
                      ? "green"
                      : priceIndicatorType === "higher"
                      ? "yellow"
                      : priceIndicatorType === "much-higher"
                      ? "red"
                      : priceIndicatorType === "lower"
                      ? "blue"
                      : priceIndicatorType === "much-lower"
                      ? "purple"
                      : "gray"
                  }
                  slim
                >
                  {priceDifferencePercent > 0 ? "+" : ""}
                  {priceDifferencePercent?.toFixed(1)}%
                </Badge>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Search Box Component that connects to InstantSearch
const SearchBox = ({ onQueryChange }) => {
  const { query, refine } = useSearchBox();
  const [localQuery, setLocalQuery] = useState(query);

  // Create debounced refine function
  const debouncedRefine = useMemo(
    () =>
      debounce((value) => {
        refine(value);
        onQueryChange(value);
      }, 500),
    [refine, onQueryChange]
  );

  const handleChange = (e) => {
    const newQuery = e.target.value;
    // Update local state immediately for responsive UI
    setLocalQuery(newQuery);
    // Debounce the actual search
    debouncedRefine(newQuery);
  };

  // Sync local query with search query when it changes
  useEffect(() => {
    setLocalQuery(query);
  }, [query]);

  return (
    <input
      type="text"
      placeholder="Or search for additional products..."
      value={localQuery}
      onChange={handleChange}
      className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-theme-surface-accent text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    />
  );
};

// Enhanced CustomDropdown with integrated search
const EnhancedCustomDropdown = ({
  value,
  onChange,
  options,
  placeholder,
  posSku,
  posPrice,
  mappedProduct, // full Saucey product object for the current mapping, if available
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    setSearchQuery("");
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    // Filter options based on search query
    if (searchQuery.trim() === "") {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(
        (option) =>
          option.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
          option.subtitle.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  }, [searchQuery, options]);

  useEffect(() => {
    // Focus search input when dropdown opens
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current.focus(), 100);
    }
  }, [isOpen]);

  const selectedOption = options.find((option) => option.value === value);

  // Build display option for the current mapping. Prefer the actual Saucey product,
  // then fall back to the selected option, then to a bare SKU.
  let displayOption = null;
  if (mappedProduct) {
    displayOption = {
      value: mappedProduct.sku_id,
      label: `${mappedProduct.title}${
        mappedProduct.volume_label ? ` - ${mappedProduct.volume_label}` : ""
      }`,
      subtitle:
        mappedProduct.subtitle ||
        mappedProduct.default_category ||
        mappedProduct.company ||
        "",
      price: mappedProduct.lowest_price || 0,
      confidence: null,
      image: mappedProduct.image,
    };
  } else if (selectedOption) {
    displayOption = selectedOption;
  } else if (value) {
    displayOption = {
      value: value,
      label: `SKU: ${value}`,
      subtitle: "Mapped product",
      price: 0,
      confidence: 1,
      image: null,
    };
  }

  const handleSearchChange = (query) => {
    setSearchQuery(query);
  };

  const handleSelectProduct = (hit) => {
    const option = {
      value: hit.sku_id,
      label: `${hit.title} - ${hit.volume_label || ""}`,
      subtitle: hit.subtitle || hit.default_category,
      price: hit.lowest_price || 0,
      confidence: 0.9,
      image: hit.image_url || hit.image,
      id: hit.sku_id, // Store SKU ID for API calls
    };
    onChange(option);
    setIsOpen(false);
    setSearchQuery("");
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 flex items-center justify-between hover:border-slate-400 dark:hover:border-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      >
        <div className="flex items-center space-x-3">
          {displayOption ? (
            <>
              {displayOption.image && (
                <img
                  src={displayOption.image}
                  alt={displayOption.label}
                  className="w-8 h-8 object-cover rounded"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              )}
              <div className="font-medium text-slate-800 dark:text-slate-100 truncate flex items-center gap-2">
                {displayOption.label}
                {displayOption.subtitle && (
                  <div className="text-sm text-slate-600 dark:text-slate-400 truncate">
                    {displayOption.subtitle}
                  </div>
                )}
                {displayOption.confidence && (
                  <Badge
                    color={
                      displayOption.confidence >= 0.9
                        ? "green"
                        : displayOption.confidence >= 0.7
                        ? "yellow"
                        : "red"
                    }
                    slim
                  >
                    {Math.round(displayOption.confidence * 100)}%
                  </Badge>
                )}
              </div>
            </>
          ) : (
            <span className="text-slate-500 dark:text-slate-400">
              {placeholder}
            </span>
          )}
        </div>
        <svg
          className={`w-4 h-4 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-md shadow-lg max-h-80 overflow-y-auto">
          {/* Search Input */}

          {/* Options List */}
          {!searchQuery && (
            <div className="max-h-60">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => {
                  // Calculate price difference for suggested products
                  let priceDifferencePercent = null;
                  let priceIndicatorType = null;

                  if (posPrice && option.price > 0) {
                    // Calculate percentage from retailer's perspective
                    // Positive = retailer charges more than average
                    // Negative = retailer charges less than average
                    priceDifferencePercent =
                      ((posPrice - option.price) / option.price) * 100;
                    priceIndicatorType = getPriceIndicatorType(
                      priceDifferencePercent
                    );
                  }

                  return (
                    <div
                      key={option.value}
                      className="px-3 py-2 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 flex items-center space-x-3"
                      onClick={() => {
                        onChange(option);
                        setIsOpen(false);
                        setSearchQuery("");
                      }}
                    >
                      {option.image && (
                        <img
                          src={option.image}
                          alt={option.label}
                          className="w-8 h-8 object-cover rounded flex-shrink-0"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-800 dark:text-slate-100 truncate flex items-center gap-2">
                          {option.label}
                          <Badge
                            color={
                              option.confidence >= 0.9
                                ? "green"
                                : option.confidence >= 0.7
                                ? "yellow"
                                : "red"
                            }
                            slim
                          >
                            {Math.round(option.confidence * 100)}% Match
                          </Badge>
                        </div>
                        {(option.subtitle || option.value) && (
                          <div className="text-sm text-slate-600 dark:text-slate-400 truncate">
                            {option.subtitle || `SKU: ${option.value}`}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0">
                        <span className="text-sm text-slate-600 dark:text-slate-400">
                          Avg ${option.price}
                        </span>
                        {priceIndicatorType && (
                          <Badge
                            color={
                              priceIndicatorType === "match"
                                ? "green"
                                : priceIndicatorType === "higher"
                                ? "yellow"
                                : priceIndicatorType === "much-higher"
                                ? "red"
                                : priceIndicatorType === "lower"
                                ? "blue"
                                : priceIndicatorType === "much-lower"
                                ? "purple"
                                : "gray"
                            }
                            slim
                          >
                            {priceDifferencePercent > 0 ? "+" : ""}
                            {priceDifferencePercent?.toFixed(1)}%
                          </Badge>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="px-3 py-4 text-center text-slate-500 dark:text-slate-400">
                  {searchQuery ? "No products found" : "No options available"}
                </div>
              )}
            </div>
          )}
          <div className="p-3 border-b border-slate-200 dark:border-slate-600">
            <SearchBox onQueryChange={handleSearchChange} />
          </div>

          {/* External Search Results */}
          {searchQuery && (
            <SearchResults
              onSelectProduct={handleSelectProduct}
              currentQuery={searchQuery}
              showAllProducts={!searchQuery || searchQuery.length < 2}
              posPrice={posPrice}
            />
          )}
        </div>
      )}
    </div>
  );
};

const getFilterButtonClass = (isActive) =>
  `px-3 py-1 rounded transition-colors duration-150 ${
    isActive
      ? "bg-indigo-500 text-white"
      : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200"
  }`;

// Wraps the Typesense search client to merge catalog (verified) + merchant custom product results
function createCatalogAndCustomSearchClient(baseClient, catalogIndexName, merchantIndexName) {
  if (!merchantIndexName) return baseClient;
  return {
    ...baseClient,
    search: async (requests) => {
      if (!requests || requests.length === 0) return baseClient.search(requests);
      const first = requests[0];
      const catalogRequest = { ...first, indexName: catalogIndexName };
      const customRequest = {
        ...first,
        indexName: merchantIndexName,
        params: {
          ...(first.params || {}),
          filters: [first.params?.filters, "is_custom:=true"].filter(Boolean).join(" && "),
        },
      };
      const { results } = await baseClient.search([catalogRequest, customRequest]);
      const catalogResult = results[0] || { hits: [] };
      const customResult = results[1] || { hits: [] };
      const seen = new Set((catalogResult.hits || []).map((h) => h.sku_id));
      const extra = (customResult.hits || []).filter((h) => h.sku_id && !seen.has(h.sku_id));
      return {
        results: [
          {
            ...catalogResult,
            hits: [...(catalogResult.hits || []), ...extra],
          },
        ],
      };
    },
  };
}

const ProductMapping = () => {
  const dispatch = useDispatch();
  const { selectedLocation, selectedMerchant } = useSelector((state) => state.app);
  const merchantIndexName = selectedMerchant?.easy_id
    ? `${config.environment}_${selectedMerchant.easy_id}_products`
    : null;
  const [posProducts, setPosProducts] = useState([]);
  const [sauceyProducts, setSauceyProducts] = useState(new Map()); // Map of SKU ID -> Product details
  const [loading, setLoading] = useState(true);
  const [searchClient, setSearchClient] = useState(null);
  const [searchClientWithoutGrouping, setSearchClientWithoutGrouping] =
    useState(null);
  const [searchClientForMapping, setSearchClientForMapping] = useState(null);
  const [filter, setFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const [createCustomOpen, setCreateCustomOpen] = useState(false);
  const [createCustomPosProduct, setCreateCustomPosProduct] = useState(null);

  // Initialize Typesense search client
  useEffect(() => {
    const typesenseInstantsearchAdapter = new TypesenseInstantSearchAdapter({
      server: {
        apiKey: config.ts.key,
        nodes: config.ts.nodes,
        nearestNode: config.ts.nearestNode,
        cacheSearchResultsForSeconds: 0,
      },
      additionalSearchParameters: {
        query_by: "title,subtitle,description,default_category,company",
        groupBy: "sku_id",
        groupLimit: 99,
      },
    });

    const typesenseInstantsearchAdapterWithoutGrouping =
      new TypesenseInstantSearchAdapter({
        server: {
          apiKey: config.ts.key,
          nodes: config.ts.nodes,
          nearestNode: config.ts.nearestNode,
          cacheSearchResultsForSeconds: 0,
        },
        additionalSearchParameters: {
          query_by: "title,subtitle,description,default_category,company",
        },
      });

    setSearchClient(typesenseInstantsearchAdapter.searchClient);
    setSearchClientWithoutGrouping(
      typesenseInstantsearchAdapterWithoutGrouping.searchClient
    );
  }, []);

  // Merge catalog + custom product search when merchant index is available
  useEffect(() => {
    if (!searchClientWithoutGrouping) {
      setSearchClientForMapping(null);
      return;
    }
    setSearchClientForMapping(
      createCatalogAndCustomSearchClient(
        searchClientWithoutGrouping,
        config.ts.index,
        merchantIndexName
      )
    );
  }, [searchClientWithoutGrouping, merchantIndexName]);

  // Fetch POS products with matches from API
  useEffect(() => {
    const fetchProducts = async () => {
      if (!selectedLocation) return;
      setLoading(true);
      try {
        const response = await getProductMappings();
        if (response.success && response.products) {
          // Map backend response to frontend structure
          const mappedProducts = response.products.map((product) => ({
            pos_id: product.pos_id,
            pos_name: product.pos_name,
            pos_sku: product.pos_id, // Use pos_id as SKU for now (backend doesn't return pos_sku)
            pos_price: parseFloat(product.pos_price) || 0,
            pos_upc: product.pos_upc || "",
            match_id: product.saucey_product_id || null,
            match_status: product.status || "not_matched",
            saucey_product_id: product.saucey_product_id || null,
            saucey_sku_id: product.saucey_sku_id || null,
            custom_product: product.custom_product ?? false,
            ignored: product.ignored === true,
          }));
          setPosProducts(mappedProducts);

          // Fetch Saucey product details for all mapped products
          const sauceySKUIds = mappedProducts
            .filter((p) => p.saucey_sku_id)
            .map((p) => p.saucey_sku_id);

          if (sauceySKUIds.length > 0) {
            try {
              const productsResponse = await getProductsBySKUs(sauceySKUIds);
              if (productsResponse.status === "success" && productsResponse.data) {
                // Create a Map of SKU ID -> Product details
                const productsMap = new Map();
                productsResponse.data.forEach((product) => {
                  productsMap.set(product.sku_id, product);
                });
                setSauceyProducts(productsMap);
              }
            } catch (error) {
              console.error("Failed to fetch Saucey product details:", error);
              // Don't show error toast for this, as it's not critical
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch POS products with matches:", error);
        dispatch(
          addToast({
            type: "error",
            message: "Failed to load product mapping data",
            timer: 5,
          })
        );
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [selectedLocation, dispatch]);

  const handleCustomProductCreated = async (skuId) => {
    if (!createCustomPosProduct) return;
    try {
      const res = await updateProductMapping(createCustomPosProduct.pos_id, skuId);
      if (res.success) {
        dispatch(addToast({ type: "success", message: "Custom product created and mapped", timer: 5 }));
        setCreateCustomOpen(false);
        setCreateCustomPosProduct(null);
        const updatedResponse = await getProductMappings();
        if (updatedResponse.success && updatedResponse.products) {
          const mappedProducts = updatedResponse.products.map((product) => ({
            pos_id: product.pos_id,
            pos_name: product.pos_name,
            pos_sku: product.pos_id,
            pos_price: parseFloat(product.pos_price) || 0,
            pos_upc: "",
            match_id: product.saucey_product_id || null,
            match_status: product.status || "not_matched",
            saucey_product_id: product.saucey_product_id || null,
            saucey_sku_id: product.saucey_sku_id || null,
            custom_product: product.custom_product ?? false,
            ignored: product.ignored === true,
          }));
          setPosProducts(mappedProducts);
          const sauceySKUIds = mappedProducts.filter((p) => p.saucey_sku_id).map((p) => p.saucey_sku_id);
          if (sauceySKUIds.length > 0) {
            const productsResponse = await getProductsBySKUs(sauceySKUIds);
            if (productsResponse.status === "success" && productsResponse.data) {
              const productsMap = new Map();
              productsResponse.data.forEach((product) => {
                productsMap.set(product.sku_id, product);
              });
              setSauceyProducts(productsMap);
            }
          }
        }
      } else {
        dispatch(addToast({ type: "error", message: res.error || "Failed to map product", timer: 5 }));
      }
    } catch (e) {
      dispatch(addToast({ type: "error", message: "Failed to apply mapping", timer: 5 }));
    }
  };

  const handleMapProduct = async (posProduct, mappedItem) => {
    if (!mappedItem || !mappedItem.value) {
      // Unmap product
      await handleUnmapProduct(posProduct);
      return;
    }

    try {
      const response = await updateProductMapping(
        posProduct.pos_id,
        mappedItem.value // SKU ID
      );
      if (response.success) {
        dispatch(
          addToast({
            type: "success",
            message: "Product mapping updated successfully",
            timer: 5,
          })
        );
        // Refresh products to reflect changes
        const updatedResponse = await getProductMappings();
        if (updatedResponse.success && updatedResponse.products) {
          const mappedProducts = updatedResponse.products.map((product) => ({
            pos_id: product.pos_id,
            pos_name: product.pos_name,
            pos_sku: product.pos_id,
            pos_price: parseFloat(product.pos_price) || 0,
            pos_upc: "",
            match_id: product.saucey_product_id || null,
            match_status: product.status || "not_matched",
            saucey_product_id: product.saucey_product_id || null,
            saucey_sku_id: product.saucey_sku_id || null,
            custom_product: product.custom_product ?? false,
            ignored: product.ignored === true,
          }));
          setPosProducts(mappedProducts);

          // Refresh Saucey product details
          const sauceySKUIds = mappedProducts
            .filter((p) => p.saucey_sku_id)
            .map((p) => p.saucey_sku_id);

          if (sauceySKUIds.length > 0) {
            try {
              const productsResponse = await getProductsBySKUs(sauceySKUIds);
              if (productsResponse.status === "success" && productsResponse.data) {
                const productsMap = new Map();
                productsResponse.data.forEach((product) => {
                  productsMap.set(product.sku_id, product);
                });
                setSauceyProducts(productsMap);
              }
            } catch (error) {
              console.error("Failed to fetch Saucey product details:", error);
            }
          } else {
            // Clear product details if no mappings
            setSauceyProducts(new Map());
          }
        }
      }
    } catch (error) {
      console.error("Failed to map product:", error);
      dispatch(
        addToast({
          type: "error",
          message: error.response?.data?.error || "Failed to map product",
          timer: 5,
        })
      );
    }
  };

  const handleUnmapProduct = async (posProduct) => {
    if (!posProduct.match_id) return; // Nothing to unmap
    try {
      const response = await deleteProductMapping(posProduct.pos_id);
      if (response.success) {
        dispatch(
          addToast({
            type: "success",
            message: "Product unmapped successfully",
            timer: 5,
          })
        );
        // Refresh products to reflect changes
        const updatedResponse = await getProductMappings();
        if (updatedResponse.success && updatedResponse.products) {
          const mappedProducts = updatedResponse.products.map((product) => ({
            pos_id: product.pos_id,
            pos_name: product.pos_name,
            pos_sku: product.pos_id,
            pos_price: parseFloat(product.pos_price) || 0,
            pos_upc: "",
            match_id: product.saucey_product_id || null,
            match_status: product.status || "not_matched",
            saucey_product_id: product.saucey_product_id || null,
            saucey_sku_id: product.saucey_sku_id || null,
            custom_product: product.custom_product ?? false,
            ignored: product.ignored === true,
          }));
          setPosProducts(mappedProducts);

          // Refresh Saucey product details
          const sauceySKUIds = mappedProducts
            .filter((p) => p.saucey_sku_id)
            .map((p) => p.saucey_sku_id);

          if (sauceySKUIds.length > 0) {
            try {
              const productsResponse = await getProductsBySKUs(sauceySKUIds);
              if (productsResponse.status === "success" && productsResponse.data) {
                const productsMap = new Map();
                productsResponse.data.forEach((product) => {
                  productsMap.set(product.sku_id, product);
                });
                setSauceyProducts(productsMap);
              }
            } catch (error) {
              console.error("Failed to fetch Saucey product details:", error);
            }
          } else {
            // Clear product details if no mappings
            setSauceyProducts(new Map());
          }
        }
      }
    } catch (error) {
      console.error("Failed to unmap product:", error);
      dispatch(
        addToast({
          type: "error",
          message: error.response?.data?.error || "Failed to unmap product",
          timer: 5,
        })
      );
    }
  };

  const handleIgnoreProduct = async (posProduct) => {
    try {
      const response = await ignoreProductMapping(posProduct.pos_id);
      if (response.success) {
        dispatch(
          addToast({
            type: "success",
            message: `'${posProduct.pos_name}' ignored. It will be deactivated in pricing and skipped in future syncs.`,
            timer: 5,
          })
        );
        const updatedResponse = await getProductMappings();
        if (updatedResponse.success && updatedResponse.products) {
          const mappedProducts = updatedResponse.products.map((product) => ({
            pos_id: product.pos_id,
            pos_name: product.pos_name,
            pos_sku: product.pos_id,
            pos_price: parseFloat(product.pos_price) || 0,
            pos_upc: "",
            match_id: product.saucey_product_id || null,
            match_status: product.status || "not_matched",
            saucey_product_id: product.saucey_product_id || null,
            saucey_sku_id: product.saucey_sku_id || null,
            custom_product: product.custom_product ?? false,
            ignored: product.ignored === true,
          }));
          setPosProducts(mappedProducts);
          const sauceySKUIds = mappedProducts
            .filter((p) => p.saucey_sku_id)
            .map((p) => p.saucey_sku_id);
          if (sauceySKUIds.length > 0) {
            try {
              const productsResponse = await getProductsBySKUs(sauceySKUIds);
              if (productsResponse.status === "success" && productsResponse.data) {
                const productsMap = new Map();
                productsResponse.data.forEach((product) => {
                  productsMap.set(product.sku_id, product);
                });
                setSauceyProducts(productsMap);
              }
            } catch (error) {
              console.error("Failed to fetch Saucey product details:", error);
            }
          } else {
            setSauceyProducts(new Map());
          }
        }
      }
    } catch (error) {
      console.error("Failed to ignore product:", error);
      dispatch(
        addToast({
          type: "error",
          message: error.response?.data?.error || "Failed to ignore product",
          timer: 5,
        })
      );
    }
  };

  const handleUnignoreProduct = async (posProduct) => {
    try {
      const response = await unignoreProductMapping(posProduct.pos_id);
      if (response.success) {
        dispatch(
          addToast({
            type: "success",
            message: `'${posProduct.pos_name}' unignored. It can be mapped again in future syncs.`,
            timer: 5,
          })
        );
        const updatedResponse = await getProductMappings();
        if (updatedResponse.success && updatedResponse.products) {
          const mappedProducts = updatedResponse.products.map((product) => ({
            pos_id: product.pos_id,
            pos_name: product.pos_name,
            pos_sku: product.pos_id,
            pos_price: parseFloat(product.pos_price) || 0,
            pos_upc: "",
            match_id: product.saucey_product_id || null,
            match_status: product.status || "not_matched",
            saucey_product_id: product.saucey_product_id || null,
            saucey_sku_id: product.saucey_sku_id || null,
            custom_product: product.custom_product ?? false,
            ignored: product.ignored === true,
          }));
          setPosProducts(mappedProducts);
          const sauceySKUIds = mappedProducts
            .filter((p) => p.saucey_sku_id)
            .map((p) => p.saucey_sku_id);
          if (sauceySKUIds.length > 0) {
            try {
              const productsResponse = await getProductsBySKUs(sauceySKUIds);
              if (productsResponse.status === "success" && productsResponse.data) {
                const productsMap = new Map();
                productsResponse.data.forEach((product) => {
                  productsMap.set(product.sku_id, product);
                });
                setSauceyProducts(productsMap);
              }
            } catch (error) {
              console.error("Failed to fetch Saucey product details:", error);
            }
          } else {
            setSauceyProducts(new Map());
          }
        }
      }
    } catch (error) {
      console.error("Failed to unignore product:", error);
      dispatch(
        addToast({
          type: "error",
          message: error.response?.data?.error || "Failed to unignore product",
          timer: 5,
        })
      );
    }
  };

  // Before mapping over posProducts, filter them:
  const filteredProducts = posProducts.filter((product) => {
    const isIgnored = product.ignored === true;
    const isMapped = product.match_id;

    if (filter === "all") return true;
    if (filter === "mapped") return isMapped;
    if (filter === "unmapped") return !isMapped && !isIgnored;
    if (filter === "ignored") return isIgnored;
    return true;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, endIndex);

  // Reset to first page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  const clientForMapping = searchClientForMapping ?? searchClientWithoutGrouping;
  if (loading || !searchClient || !clientForMapping) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <InstantSearch
      indexName={config.ts.index}
      searchClient={clientForMapping}
    >
      <Configure hitsPerPage={50} query="" />

      <div>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">
              Product Mapping
            </h2>
            <p className="text-slate-600 dark:text-slate-300 mt-1">
              Map your POS products to Saucey products
            </p>
          </div>
          <div className="text-sm text-slate-500 dark:text-slate-400">
            {posProducts.filter((p) => p.match_id).length} mapped,{" "}
            {posProducts.filter((p) => p.ignored).length} ignored,{" "}
            {posProducts.filter((p) => !p.match_id && !p.ignored).length}{" "}
            unmapped
          </div>
        </div>

        <div className="flex space-x-2 mb-4">
          <button
            className={getFilterButtonClass(filter === "all")}
            onClick={() => setFilter("all")}
          >
            All
          </button>
          <button
            className={getFilterButtonClass(filter === "mapped")}
            onClick={() => setFilter("mapped")}
          >
            Mapped
          </button>
          <button
            className={getFilterButtonClass(filter === "unmapped")}
            onClick={() => setFilter("unmapped")}
          >
            Unmapped
          </button>
          <button
            className={getFilterButtonClass(filter === "ignored")}
            onClick={() => setFilter("ignored")}
          >
            Ignored
          </button>
        </div>

        <div className="bg-white dark:bg-theme-surface-accent shadow-lg rounded-sm border border-slate-200 dark:border-slate-700">
          <div className="p-6">
            <div className="space-y-4">
              {paginatedProducts.map((product) => {
                const isIgnored = product.ignored === true;
                const isMapped = product.match_id;
                const mappedSkuId = product.saucey_sku_id || null;
                // Get Saucey product details if mapped
                const sauceyProduct = mappedSkuId ? sauceyProducts.get(mappedSkuId) : null;

                // Dropdown options will be populated via Typesense search
                const dropdownOptions = [];

                return (
                  <div
                    key={product.pos_id}
                    className={`border rounded-lg p-4 ${
                      isIgnored
                        ? "bg-gray-50 dark:bg-gray-800/50 border-gray-300 dark:border-gray-600"
                        : "border-slate-200 dark:border-slate-700"
                    }`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h3
                          className={`font-semibold ${
                            isIgnored
                              ? "text-gray-500 dark:text-gray-400"
                              : "text-slate-800 dark:text-slate-100"
                          }`}
                        >
                          {product.pos_name}
                        </h3>
                        <div className="flex items-center space-x-4 mt-1 text-sm text-slate-600 dark:text-slate-400">
                          <span>
                            POS ID:{" "}
                            <code className="bg-slate-100 dark:bg-slate-700 px-1 rounded">
                              {product.pos_id}
                            </code>
                          </span>
                          {product.pos_upc && <span>UPC: {product.pos_upc}</span>}
                          <span>Price: ${product.pos_price?.toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {isIgnored ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200">
                            Ignored
                          </span>
                        ) : product.match_status === "auto" ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            Auto-Mapped
                          </span>
                        ) : product.match_status === "manual" ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            Manually Mapped
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                            Unmapped
                          </span>
                        )}
                      </div>
                    </div>

                    {!isIgnored ? (
                        <div className="flex items-center space-x-4">
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            Select Saucey Product Match
                          </label>
                          <div className="relative">
                            <EnhancedCustomDropdown
                              value={mappedSkuId || ""}
                              onChange={(item) => handleMapProduct(product, item)}
                              options={dropdownOptions}
                              placeholder="-- Select a product --"
                              posSku={product.pos_sku}
                              posPrice={product.pos_price}
                              mappedProduct={sauceyProduct}
                            />
                          </div>
                        </div>
                        <div className="flex items-end gap-2">
                          <SecondaryButton
                            type="button"
                            onClick={() => {
                              setCreateCustomPosProduct(product);
                              setCreateCustomOpen(true);
                            }}
                            additionalClasses="border border-purple-300 text-purple-600 hover:text-purple-800 hover:border-purple-400"
                          >
                            Create custom product
                          </SecondaryButton>
                          <SecondaryDangerButton
                            onClick={() => handleIgnoreProduct(product)}
                            additionalClasses="px-3 py-2 text-sm"
                          >
                            Ignore
                          </SecondaryDangerButton>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          This product has been ignored and will be skipped
                          during import.
                        </p>
                        <NakedButton
                          onClick={() => handleUnignoreProduct(product)}
                          additionalClasses="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 hover:border-gray-400 rounded-md transition-colors"
                        >
                          Unignore
                        </NakedButton>
                      </div>
                    )}

                    {isMapped && mappedSkuId && (
                      <div className="mt-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                        <div className="flex items-center space-x-3">
                          {sauceyProduct?.image && (
                            <img
                              src={sauceyProduct.image}
                              alt={sauceyProduct.title || mappedSkuId}
                              className="w-12 h-12 object-cover rounded-lg flex-shrink-0"
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                          )}
                          <div className="flex-1">
                            <h4 className="font-medium text-green-800 dark:text-green-200 flex items-center gap-2">
                              Mapped to: {sauceyProduct?.title || mappedSkuId}
                              {sauceyProduct?.volume_label && ` - ${sauceyProduct.volume_label}`}
                              {product.custom_product && (
                                <Badge color="purple" slim>Custom</Badge>
                              )}
                            </h4>
                            {sauceyProduct?.subtitle && (
                              <p className="text-sm text-green-600 dark:text-green-300 mt-1">
                                {sauceyProduct.subtitle}
                              </p>
                            )}
                            <p className="text-xs text-green-500 dark:text-green-400 mt-1">
                              SKU: {mappedSkuId}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <NakedButton
                              onClick={() => handleUnmapProduct(product)}
                              additionalClasses="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 hover:border-gray-400 rounded-md transition-colors"
                            >
                              Unmap
                            </NakedButton>
                          </div>
                        </div>
                      </div>
                    )}

                    {!isIgnored && !isMapped && (
                      <div className="mt-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3">
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          No automatic match found. Type in the dropdown to search for products.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex justify-center">
            <PaginationNumeric
              currentPage={currentPage - 1}
              onSelect={(page) => setCurrentPage(page + 1)}
              totalPages={totalPages}
              isFirstPage={currentPage === 1}
              isLastPage={currentPage === totalPages}
            />
          </div>
        )}
      </div>

      {/* Create custom product modal (reuses products page UI) */}
      {createCustomOpen && createCustomPosProduct && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={(e) => e.target === e.currentTarget && setCreateCustomOpen(false)}
        >
          <div className="bg-white dark:bg-theme-surface-accent rounded-lg shadow-xl border border-slate-200 dark:border-slate-600 w-full max-w-5xl max-h-[90vh] flex flex-col">
            <div className="flex-shrink-0 px-4 py-3 border-b border-slate-200 dark:border-slate-600 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                Create custom product — mapping to: <strong>{createCustomPosProduct.pos_name}</strong>
              </h3>
              <NakedButton
                type="button"
                onClick={() => {
                  setCreateCustomOpen(false);
                  setCreateCustomPosProduct(null);
                }}
                additionalClasses="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 p-1"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </NakedButton>
            </div>
            <div className="flex-1 overflow-y-auto min-h-0">
              <CustomProduct
                embedded
                posProduct={createCustomPosProduct}
                onCreated={handleCustomProductCreated}
                onCancel={() => {
                  setCreateCustomOpen(false);
                  setCreateCustomPosProduct(null);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </InstantSearch>
  );
};

export default ProductMapping;
